import { gql, useApolloClient } from '@apollo/client';
import { createEndpoint, fromIframe } from '@remote-ui/rpc';
import {
  createIframeWorkerMessenger,
  createWorkerFactory,
  terminate,
} from '@shopify/web-worker';
import { ExecutionResult } from 'graphql';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import { ExplorerBehavioralFlags } from 'src/app/graph/explorerPage/explorerBehavioralFlags';
import { headersWithContentType } from 'src/app/graph/explorerPage/helpers/headersWithContentType';
import { parseVariablesOrDefault } from 'src/app/graph/explorerPage/helpers/jsonHelpers';
import { JSONObject } from 'src/app/graph/explorerPage/helpers/types';
import { traverseVariablesAndAttemptInterpolation } from 'src/app/graph/explorerPage/hooks/useExplorerState/interpolation/traverseVariablesAndAttemptInterpolation';
import {
  OperationRequestBody,
  performOperation,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/performOperation/performOperation';
import { performPostMessageOperation } from 'src/app/graph/explorerPage/hooks/useExplorerState/performPostMessageOperation';
import {
  headersObjectFromDefinitions,
  useHeadersManagerContext,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import type {
  InternalRunOperationParams,
  RemoteFetch,
  RunOperationParams,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/usePreflightSandbox/preflightWorker';
import { GraphRef, parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { useShowToast } from 'src/components/toast/Toast';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';

import { ExplorerDocsTab } from '../../../helpers/ExplorerDocsTab';

const REMOTE_FETCH_URL = 'https://preflight-request.apollographql.com/';
const MAX_CALLS_TO_SAME_OPERATION_SCRIPTS = 10;
const MAX_SAME_PATH_TO_OPERATION_SCRIPT = 2;

const createWorker = createWorkerFactory(
  () => import(/* webpackChunkName: 'preflightWorker' */ './preflightWorker'),
);

/**
 * This function will return if we detect a cycle to prevent infinite loops.
 * There is a max calls to a a single operation, but then a more strict check if
 * it's the same path to the operation because it's more likely that will not recover
 */
export function detectOperationScriptCycle(
  previousOperations: string[],
  currentOperationId: string,
) {
  const allPathsToOperation = previousOperations.reduce(
    (acc: string[][], previousOperationId) => {
      if (previousOperationId === currentOperationId) {
        return [...acc, []];
      } else {
        const last = acc.pop() ?? [];
        return [...acc, [...last, previousOperationId]];
      }
    },
    [],
  );
  const maxInstancesOfPath = Math.max(
    0,
    ...Object.values(_.countBy(allPathsToOperation)),
  );

  const callsToOperation =
    _.countBy(previousOperations)[currentOperationId] ?? 0;
  return (
    maxInstancesOfPath > MAX_SAME_PATH_TO_OPERATION_SCRIPT ||
    callsToOperation > MAX_CALLS_TO_SAME_OPERATION_SCRIPTS
  );
}
const hostnameBlockList = ['apollo', 'apollographql'];
function validateFetchUrl(url: unknown) {
  if (typeof url !== 'string') {
    throw new Error('the url passed to explorer.fetch must be a string');
  }
  const requestHost = new URL(url).host;

  if (
    hostnameBlockList.some((blockedItem) => requestHost.includes(blockedItem))
  ) {
    throw new Error(
      `Requests cannot be made against hosts with names including ${hostnameBlockList.join()}`,
    );
  }
}

function useRemoteSandboxFetch() {
  const endpoint = useMemo(() => {
    try {
      // Catch errors throw by creating iframes, this prevents users who aren't
      // using preflight scripts being blocked by a bug where the iframe is throwing an
      // error on creation because of a one trust monkey patch.
      //
      // We can't lazily initialize this because we end up with a race condition where the bridge between the main thread and iframe doesn't get connected
      //
      // some context: https://apollograph.slack.com/archives/C02852RRUKW/p1637717745234200
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', REMOTE_FETCH_URL);
      iframe.style.display = 'none';
      document.body.appendChild(iframe); // iframe will be removed when the endpoint is passed to `terminate`

      return createEndpoint<{
        sandboxFetch: RemoteFetch;
      }>(fromIframe(iframe));
    } catch (err) {
      return null;
    }
  }, []);

  const remoteSandboxFetch = useCallback(
    async (...args: Parameters<RemoteFetch>) => {
      validateFetchUrl(args[0]);

      if (process.env.NODE_ENV === 'test') {
        // For unit tests, make regular request that can be mocked
        return fetch(...args).then(async (r) => {
          const textContent = await r.text();
          return {
            body: textContent,
            json: () => JSON.parse(textContent),
            code: r.status,
            headers: Object.fromEntries([...r.headers.entries()]),
          };
        });
      }
      if (!endpoint) {
        throw new Error(
          'Failed to initialize preflight scripts, please contact support',
        );
      }
      return endpoint.call.sandboxFetch(...args);
    },
    [endpoint],
  );

  useEffect(() => {
    return () => endpoint?.terminate();
  }, [endpoint]);

  return remoteSandboxFetch;
}

export const UsePreflightSandboxOperationCollectionFragment = gql`
  fragment UsePreflightSandboxOperationCollectionFragment on OperationCollection {
    id
    name
    isSandbox
    isShared
    operations {
      id
      name
    }
  }
`;

const LoadSandboxOperationCollections = gql<
  GraphQLTypes.LoadSandboxOperationCollections,
  GraphQLTypes.LoadSandboxOperationCollectionsVariables
>`
  query LoadSandboxOperationCollections {
    me {
      id
      ... on User {
        sandboxOperationCollections {
          id
          ...UsePreflightSandboxOperationCollectionFragment
        }
      }
    }
  }
  ${UsePreflightSandboxOperationCollectionFragment}
`;

export const LoadRegisteredOperationCollections = gql<
  GraphQLTypes.LoadRegisteredOperationCollections,
  GraphQLTypes.LoadRegisteredOperationCollectionsVariables
>`
  query LoadRegisteredOperationCollections(
    $graphId: ID!
    $graphVariant: String!
  ) {
    service(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        operationCollections {
          id
          ...UsePreflightSandboxOperationCollectionFragment
        }
      }
    }
  }
  ${UsePreflightSandboxOperationCollectionFragment}
`;

export function usePreflightSandbox({
  endpointUrl,
  includeCookies,
  explorerBehavioralFlags,
  graphRef,
  currentOperationId,
  isOperationScriptEnabled,
  setDocsTab,
  isInTestContext,
}: {
  endpointUrl: string | null;
  includeCookies: boolean;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  graphRef: GraphRef | null;
  currentOperationId: string | undefined;
  isOperationScriptEnabled: boolean;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  isInTestContext: boolean;
}) {
  const {
    interpolateEnvVariablesIntoHeadersWithRef,
    updateEnvironmentVariables,
    sharedHeaderDefinitions,
  } = useHeadersManagerContext();
  const remoteSandboxFetch = useRemoteSandboxFetch();

  const apolloClient = useApolloClient();

  const loadSavedOperation = useCallback(
    (variables: GraphQLTypes.LoadOperationCollectionEntryVariables) => {
      return apolloClient.query({
        query: gql<
          GraphQLTypes.LoadOperationCollectionEntry,
          GraphQLTypes.LoadOperationCollectionEntryVariables
        >`
          query LoadOperationCollectionEntry($collectionEntryIds: [ID!]!) {
            operationCollectionEntries(
              collectionEntryIds: $collectionEntryIds
            ) {
              id
              collection {
                id
                isSandbox
                isShared
                variants {
                  id
                }
              }
              currentOperationRevision {
                body
                variables
                script
                headers {
                  name
                  value
                }
                postflightOperationScript
              }
            }
          }
        `,
        variables,
      });
    },
    [apolloClient],
  );

  const runInSandbox = useCallback(
    (...args: Parameters<ReturnType<typeof createWorker>['execute']>) => {
      const worker = createWorker({
        createMessenger: createIframeWorkerMessenger,
      });
      return worker.execute(...args).finally(() => terminate(worker));
    },
    [],
  );

  const performRealOperation = useCallback(
    async ({
      operationString,
      variables,
      environmentVariables,
      headers,
      operationName,
    }: {
      operationString: string;
      variables: JSONObject;
      environmentVariables: JSONObject;
      headers: Record<string, string>;
      operationName: string | undefined;
    }) => {
      const processedHeaders = interpolateEnvVariablesIntoHeadersWithRef(
        headersWithContentType(headers),
      );

      if (!endpointUrl) {
        throw new Error('endpoint url missing');
      }

      const processedVariables = await traverseVariablesAndAttemptInterpolation(
        variables,
        environmentVariables,
      );

      if (explorerBehavioralFlags.shouldPostMessageOperations) {
        return performPostMessageOperation({
          operationName,
          operationString,
          variables: processedVariables,
          processedHeaders,
          includeCookies,
          endpointUrl,
          requestQueryPlan: false,
          showTraces: false,
          // We don't want to support files in preflight scripts
          fileVariables: undefined,
          onResponseUpdate: undefined,
          requestConnectorsDebugging: false,
        });
      }

      return performOperation({
        requestBody: {
          query: operationString,
          variables: processedVariables,
          operationName,
        },
        url: endpointUrl,
        processedHeaders,
        includeCookies,
        abortSignal: undefined,
        showTraces: false,
        requestQueryPlan: false,
        isMultipartSubscription: false,
        onResponseUpdate: undefined,
        requestConnectorsDebugging: false,
      });
    },
    [
      interpolateEnvVariablesIntoHeadersWithRef,
      endpointUrl,
      explorerBehavioralFlags.shouldPostMessageOperations,
      includeCookies,
    ],
  );
  const { showToasts } = useShowToast();
  const shouldShowPostflightScripts = useLDFlag('nebula-postflight-scripts');
  const runOperation = useCallback(
    async ({
      collectionName,
      operationName,
      scope: userScope,
      graphRef: userGraphRef,
      headers: userHeaders,
      variables: userVariables,
      oauth2Request,
      prompt,
      log,
      environmentVariables: currentEnvironmentVariables,
      previousOperations,
    }: RunOperationParams & InternalRunOperationParams) => {
      const savedCollection = currentOperationId
        ? (
            await loadSavedOperation({
              collectionEntryIds: [currentOperationId],
            })
          ).data?.operationCollectionEntries[0]?.collection
        : null;
      const currentScope =
        savedCollection?.isSandbox || (!graphRef && !savedCollection)
          ? 'sandbox'
          : savedCollection?.isShared
            ? 'shared'
            : 'personal';

      const scope = userScope ?? (userGraphRef === null ? 'sandbox' : null);
      if (currentScope === 'shared' && scope !== 'shared') {
        if (!isInTestContext) {
          showToasts({
            heading: `A ${scope} operation has been called`,
            level: 'error',
            message: `This script called an operation saved in a ${scope} collection. Scripts for shared operations can only reference other shared operations.`,
          });
        }
        throw new Error(
          'Not allowed to call non shared collections from shared ones',
        );
      } else if (!scope) {
        throw new Error('scope is required when graphRef !== null');
      }

      const parsedGraphRef =
        userGraphRef === null
          ? null
          : userGraphRef === undefined
            ? scope === 'sandbox'
              ? null
              : undefined
            : parseGraphRef(userGraphRef);

      if (parsedGraphRef === undefined) {
        throw new Error("graphRef is required when scope !== 'sandbox'");
      }
      const fetchedOperationData = await (parsedGraphRef
        ? apolloClient.query({
            query: LoadRegisteredOperationCollections,
            variables: parsedGraphRef,
          })
        : apolloClient.query({ query: LoadSandboxOperationCollections }));
      const collectionsData =
        fetchedOperationData.data && 'service' in fetchedOperationData.data
          ? fetchedOperationData.data.service?.variant?.operationCollections
          : fetchedOperationData.data?.me?.__typename === 'User'
            ? fetchedOperationData.data.me.sandboxOperationCollections
            : [];
      const matchingOperations = collectionsData?.flatMap((collection) => {
        if (
          collection.name === collectionName &&
          (scope === 'shared'
            ? collection.isShared
            : scope === 'sandbox'
              ? true
              : scope === 'personal'
                ? !collection.isSandbox
                : false)
        ) {
          return collection.operations.filter(
            (operation) => operation.name === operationName,
          );
        } else {
          return [];
        }
      });
      if (!isMinLengthArray(1, matchingOperations)) {
        throw new Error(
          `Collection "${collectionName}"/"${operationName}" not found in ${scope} collections`,
        );
      } else if (matchingOperations.length > 1) {
        showToasts({
          heading: `Multiple operations found with the same name`,
          level: 'warn',
          message: `We found multiple operations with the name  "${collectionName}"/"${operationName}", please rename one of them to make the names unique`,
        });
      }

      const result = await loadSavedOperation({
        collectionEntryIds: [matchingOperations[0].id],
      });

      const operations = result?.data?.operationCollectionEntries;

      if (!isMinLengthArray(1, operations)) {
        throw new Error('Operation not found');
      }

      const [operation] = operations;
      const {
        body: query,
        variables,
        headers,
        script,
        postflightOperationScript,
      } = operation.currentOperationRevision;

      const parsedVariables = {
        ...parseVariablesOrDefault(variables),
        ...userVariables,
      };

      let additionalScriptsCalled = 0;
      let additionalOperationsCalled = 1;
      let maxScriptDepth = previousOperations.length;
      let environmentVariables = currentEnvironmentVariables;
      let shouldShowToast = false;
      if (script && isOperationScriptEnabled) {
        if (detectOperationScriptCycle(previousOperations, operation.id)) {
          throw new Error('operation script cycle detected');
        }
        additionalScriptsCalled += 1;
        const preflightOperationScriptResults = await runInSandbox({
          script,
          environmentVariables: currentEnvironmentVariables,
          fetch: remoteSandboxFetch,
          runOperation,
          oauth2Request,
          prompt,
          log,
          requestBody: {
            query,
            variables: parsedVariables,
            operationName: undefined,
          },
          previousOperations: [...previousOperations, operation.id],
        });
        maxScriptDepth = preflightOperationScriptResults.maxScriptDepth;
        additionalScriptsCalled +=
          preflightOperationScriptResults.additionalScriptsCalled;
        additionalOperationsCalled +=
          preflightOperationScriptResults.additionalOperationsCalled;
        // TODO: not sure we actually want to do these incremental updates, we may want to update header interpolation to accept environment variables so we can update once.
        if (!_.isEqual(environmentVariables, updateEnvironmentVariables)) {
          environmentVariables =
            preflightOperationScriptResults.environmentVariables;
          updateEnvironmentVariables(
            preflightOperationScriptResults.environmentVariables,
          );
        }
      } else if (script && !isOperationScriptEnabled) {
        shouldShowToast = true;
      }

      const response = await performRealOperation({
        environmentVariables,
        operationString: query,
        variables: parsedVariables,
        headers: {
          ...headersObjectFromDefinitions(
            sharedHeaderDefinitions.map((header) => ({
              ...header,
              checked: true,
            })),
          ),
          ...Object.fromEntries(
            headers
              ?.filter((header) => !!header.name.trim() && header.value)
              .map((header) => [header.name, header.value]) ?? [],
          ),
          ...userHeaders,
        },
        operationName: undefined,
      });

      if (
        shouldShowPostflightScripts &&
        postflightOperationScript &&
        isOperationScriptEnabled &&
        response.type !== 'cancelled'
      ) {
        if (detectOperationScriptCycle(previousOperations, operation.id)) {
          throw new Error('operation script cycle detected');
        }
        additionalScriptsCalled += 1;
        const postflightOperationScriptResults = await runInSandbox({
          script: postflightOperationScript,
          environmentVariables: currentEnvironmentVariables,
          fetch: remoteSandboxFetch,
          runOperation,
          oauth2Request,
          prompt,
          log,
          requestBody: {
            query,
            variables: parsedVariables,
            operationName: undefined,
          },
          previousOperations: [...previousOperations, operation.id],
          responseBody: response.body,
        });
        maxScriptDepth = postflightOperationScriptResults.maxScriptDepth;
        additionalScriptsCalled +=
          postflightOperationScriptResults.additionalScriptsCalled;
        additionalOperationsCalled +=
          postflightOperationScriptResults.additionalOperationsCalled;
        // TODO: not sure we actually want to do these incremental updates, we may want to update header interpolation to accept environment variables so we can update once.
        if (!_.isEqual(environmentVariables, updateEnvironmentVariables)) {
          environmentVariables =
            postflightOperationScriptResults.environmentVariables;
          updateEnvironmentVariables(
            postflightOperationScriptResults.environmentVariables,
          );
        }
      } else if (
        shouldShowPostflightScripts &&
        !script &&
        postflightOperationScript &&
        !isOperationScriptEnabled &&
        response.type !== 'cancelled'
      ) {
        shouldShowToast = true;
      }

      if (shouldShowToast) {
        showToasts({
          heading: `Operation Scripts are turned off`,
          level: `warn`,
          message: (
            <span>
              One or more of the operations you're trying to call has a script
              that's currently turned off. To include the script when running
              the operation, please enable it by visiting your{' '}
              <ClickableText
                as="a"
                onClick={() => {
                  setDocsTab('Explorer Settings');
                }}
                href="#scripts"
                className="inline cursor-pointer text-link hover:underline"
              >
                Personal Settings
              </ClickableText>
              .
            </span>
          ),
          stableId: `scripts-turned-off`,
        });
      }

      return {
        environmentVariables,
        code: 'status' in response ? response.status : undefined,
        result: response.type === 'result' ? response.body : undefined,
        additionalScriptsCalled,
        additionalOperationsCalled,
        maxScriptDepth,
      };
    },
    [
      currentOperationId,
      loadSavedOperation,
      graphRef,
      apolloClient,
      isOperationScriptEnabled,
      performRealOperation,
      sharedHeaderDefinitions,
      showToasts,
      runInSandbox,
      remoteSandboxFetch,
      updateEnvironmentVariables,
      setDocsTab,
      isInTestContext,
      shouldShowPostflightScripts,
    ],
  );

  const trackCustomEvent = useTrackCustomEvent();
  return useCallback(
    async ({
      script,
      environmentVariables,
      handlePrompt,
      handleOAuth2Request,
      handleLogs = _.noop,
      requestBody,
      responseBody,
      scriptType,
      isTest,
    }: {
      script: string | undefined | null;
      environmentVariables: JSONObject;
      handleOAuth2Request: (
        authUrl: string,
        queryParams: Record<string, string>,
      ) => Promise<Record<string, string | string[] | null> | null>;
      handlePrompt: (
        msg: string,
        defaultResponse?: string,
      ) => Promise<string | null>;
      handleLogs?: (level: 'log' | 'warn' | 'error', ...msgs: string[]) => void;
      requestBody: OperationRequestBody;
      responseBody?: ExecutionResult<JSONObject> | null;
      scriptType: 'preflight' | 'preflightOperation' | 'postflightOperation';
      isTest: boolean;
    }) => {
      if (script) {
        const response = await runInSandbox({
          script,
          environmentVariables,
          /**
           * The below functions are callbacks that are triggered by the web worker.
           * How is that communication possible if the web worker is on another thread?
           *
           * Shopify uses @remote-ui/rpc under the hood that postMessages args for functions
           * from the webworker to studio.apollographql.com & triggers these functions to run
           * https://github.com/Shopify/remote-ui/tree/main/packages/rpc#function-arguments
           *
           * So you don't have to handle PMing between the webworker & the main thread yourself
           */
          fetch: remoteSandboxFetch,
          runOperation,
          oauth2Request: handleOAuth2Request,
          prompt: handlePrompt,
          log: handleLogs,
          requestBody,
          responseBody,
          previousOperations: [],
        });
        trackCustomEvent({
          action: 'script run',
          category: 'Explorer',
          label: `isTest:${isTest},scriptType:${scriptType},additionalScriptsCalled:${
            response.additionalScriptsCalled
          },additionalOperationsCalled:${
            response.additionalOperationsCalled
          },maxScriptDepth:${response.maxScriptDepth + 1}`,
        });
        return response.environmentVariables;
      }
      return environmentVariables;
    },
    [runInSandbox, remoteSandboxFetch, runOperation, trackCustomEvent],
  );
}
